export type ObservabilityLevel = 'info' | 'warning' | 'error';

export type ObservabilityPrimitive = string | number | boolean | null;

export type ObservabilityTags = Record<string, ObservabilityPrimitive>;

export type ObservabilityContext = Record<string, ObservabilityPrimitive>;

export type RuntimeObservabilityEvent = {
  name: string;
  level: ObservabilityLevel;
  tags?: ObservabilityTags;
  context?: ObservabilityContext;
};

export type RuntimeObservabilityBreadcrumb = {
  category: string;
  message: string;
  level?: ObservabilityLevel;
  data?: ObservabilityContext;
};

export type RuntimeObservabilityHandlers = {
  captureEvent: (event: RuntimeObservabilityEvent) => void;
  addBreadcrumb?: (breadcrumb: RuntimeObservabilityBreadcrumb) => void;
};

type ZodIssueLike = {
  code?: unknown;
  path?: unknown;
};

type ZodErrorLike = Error & {
  issues?: unknown;
};

type BackendErrorMetadata = {
  status: number | null;
  errorCode: string | null;
  requestId: string | null;
  endpoint: string | null;
  method: string | null;
};

const MAX_ZOD_ISSUES = 10;
const MAX_PATH_SEGMENTS = 8;
const REDACTED_PATH_SEGMENT = ':id';
const CODE_SHAPED_VALUE_PATTERN = /^(?=.*[._-])[A-Z0-9][A-Z0-9._-]*$/;
const DYNAMIC_PARENT_SEGMENTS = new Set([
  'accounts',
  'bank-accounts',
  'beneficiaries',
  'clients',
  'documents',
  'files',
  'identities',
  'organizations',
  'users',
]);

let runtimeObservabilityHandlers: RuntimeObservabilityHandlers | null = null;

export const registerRuntimeObservabilityHandlers = (handlers: RuntimeObservabilityHandlers) => {
  runtimeObservabilityHandlers = handlers;
};

export const resetRuntimeObservabilityHandlers = () => {
  runtimeObservabilityHandlers = null;
};

export const captureRuntimeEvent = (event: RuntimeObservabilityEvent) => {
  try {
    runtimeObservabilityHandlers?.captureEvent(event);
  } catch {
    // Observability must never affect app/auth/payment control flow.
  }
};

export const addRuntimeBreadcrumb = (breadcrumb: RuntimeObservabilityBreadcrumb) => {
  try {
    runtimeObservabilityHandlers?.addBreadcrumb?.(breadcrumb);
  } catch {
    // Observability must never affect app/auth/payment control flow.
  }
};

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const getNestedObject = (value: unknown, key: string): Record<string, unknown> | null => {
  if (!isObject(value)) {
    return null;
  }

  const nested = value[key];
  return isObject(nested) ? nested : null;
};

const getNestedString = (value: unknown, key: string): string | null => {
  if (!isObject(value)) {
    return null;
  }

  const nested = value[key];
  return typeof nested === 'string' && nested.length > 0 ? nested : null;
};

const getNestedNumber = (value: unknown, key: string): number | null => {
  if (!isObject(value)) {
    return null;
  }

  const nested = value[key];
  return typeof nested === 'number' ? nested : null;
};

const getHeaderValue = (headers: unknown, headerName: string): string | null => {
  if (!isObject(headers)) {
    return null;
  }

  const matchingEntry = Object.entries(headers).find(([key]) => key.toLowerCase() === headerName);
  const value = matchingEntry?.[1];

  return typeof value === 'string' && value.length > 0 ? value : null;
};

const getBackendErrorCode = (error: unknown): string | null => {
  const errorPayload = getNestedObject(error, 'error');
  const response = getNestedObject(error, 'response');
  const responseData = response?.data;

  const code =
    getNestedString(errorPayload, 'code') ??
    getNestedString(getNestedObject(errorPayload, 'error'), 'code') ??
    getNestedString(responseData, 'code') ??
    getNestedString(getNestedObject(responseData, 'error'), 'code') ??
    (typeof errorPayload?.error === 'string' ? errorPayload.error : null);

  return code !== null && CODE_SHAPED_VALUE_PATTERN.test(code) ? code : null;
};

const shouldRedactPathSegment = (segment: string, previousSegment?: string): boolean => {
  if (previousSegment && DYNAMIC_PARENT_SEGMENTS.has(previousSegment)) {
    return true;
  }

  if (segment.includes('.') || segment.includes('@') || segment.includes('%')) {
    return true;
  }

  if (segment.length >= 16) {
    return true;
  }

  if (/^[0-9]+$/.test(segment)) {
    return true;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment);
};

const toRedactedEndpoint = (url: string | null): string | null => {
  if (url === null) {
    return null;
  }

  let pathname: string;

  try {
    pathname = url.startsWith('http') ? new URL(url).pathname : (url.split('?')[0] ?? url);
  } catch {
    pathname = url.split('?')[0] ?? url;
  }

  const segments = pathname
    .split('/')
    .filter((segment) => segment.length > 0)
    .slice(0, MAX_PATH_SEGMENTS);
  const redactedSegments = segments.map((segment, index) =>
    shouldRedactPathSegment(segment, segments[index - 1]) ? REDACTED_PATH_SEGMENT : segment,
  );

  return `/${redactedSegments.join('/')}`;
};

export const getBackendErrorMetadata = (error: unknown): BackendErrorMetadata => {
  const response = getNestedObject(error, 'response');
  const config = getNestedObject(error, 'config');
  const headers = response?.headers;

  return {
    status: getNestedNumber(response, 'status') ?? getNestedNumber(error, 'status'),
    errorCode: getBackendErrorCode(error),
    requestId:
      getHeaderValue(headers, 'x-request-id') ??
      getHeaderValue(headers, 'x-correlation-id') ??
      getHeaderValue(headers, 'x-amzn-trace-id'),
    endpoint: toRedactedEndpoint(getNestedString(config, 'url')),
    method: getNestedString(config, 'method')?.toUpperCase() ?? null,
  };
};

export const isZodError = (error: unknown): error is ZodErrorLike => {
  return error instanceof Error && error.name === 'ZodError';
};

const toZodIssues = (error: ZodErrorLike): ZodIssueLike[] => {
  if (!Array.isArray(error.issues)) {
    return [];
  }

  return error.issues.filter(isObject).slice(0, MAX_ZOD_ISSUES);
};

const toZodIssuePath = (issue: ZodIssueLike): string => {
  if (!Array.isArray(issue.path)) {
    return '<unknown>';
  }

  const path = issue.path
    .slice(0, MAX_PATH_SEGMENTS)
    .map((segment) => String(segment))
    .join('.');

  return path.length > 0 ? path : '<root>';
};

export const reportZodValidationError = (input: {
  error: unknown;
  area: string;
  operation: string;
  source?: string;
  tags?: ObservabilityTags;
}) => {
  if (!isZodError(input.error)) {
    return;
  }

  const issues = toZodIssues(input.error);

  captureRuntimeEvent({
    name: 'runtime_zod_validation_failed',
    level: 'error',
    tags: {
      area: input.area,
      operation: input.operation,
      source: input.source ?? null,
      issueCount: issues.length,
      ...input.tags,
    },
    context: {
      issueCodes: issues
        .map((issue) => (typeof issue.code === 'string' ? issue.code : 'unknown'))
        .join(','),
      issuePaths: issues.map(toZodIssuePath).join(','),
    },
  });
};

export const reportBackendFailure = (input: {
  error: unknown;
  area: string;
  operation: string;
  source?: string;
  tags?: ObservabilityTags;
}) => {
  const metadata = getBackendErrorMetadata(input.error);

  captureRuntimeEvent({
    name: 'backend_request_failed',
    level: 'warning',
    tags: {
      area: input.area,
      operation: input.operation,
      source: input.source ?? null,
      backendStatus: metadata.status,
      backendErrorCode: metadata.errorCode,
      backendRequestId: metadata.requestId,
      endpoint: metadata.endpoint,
      method: metadata.method,
      ...input.tags,
    },
  });
};

export const reportFlowBlocked = (input: {
  area: string;
  operation: string;
  failureKind: string;
  level?: ObservabilityLevel;
  tags?: ObservabilityTags;
}) => {
  captureRuntimeEvent({
    name: 'flow_blocked',
    level: input.level ?? 'warning',
    tags: {
      area: input.area,
      operation: input.operation,
      failureKind: input.failureKind,
      ...input.tags,
    },
  });
};

export const reportAuthFlowBlocked = (input: {
  operation: string;
  failureKind: string;
  level?: ObservabilityLevel;
  tags?: ObservabilityTags;
}) => {
  reportFlowBlocked({
    area: 'auth',
    operation: input.operation,
    failureKind: input.failureKind,
    level: input.level,
    tags: input.tags,
  });
};
